import fabric from '@/lib/fabric';
import { isCropOverlay } from '@/lib/cropTool';
import { isMarqueeOverlay } from '@/lib/marqueeTool';
import { isObjectLocked, LAYER_CUSTOM_PROPS } from '@/lib/layerOps';

type ArrangeDir = 'front' | 'forward' | 'backward' | 'back';

let clipboardJson: Record<string, unknown> | null = null;

function editableTargets(canvas: fabric.Canvas): fabric.Object[] {
  return canvas.getActiveObjects().filter((obj) => !isObjectLocked(obj));
}

function offsetClone(obj: fabric.Object, dx = 24, dy = 24): void {
  obj.set({
    left: (obj.left || 0) + dx,
    top: (obj.top || 0) + dy,
  });
  const meta = obj as fabric.Object & { __uid?: string; craftaraLocked?: boolean };
  meta.__uid = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  if (meta.craftaraLocked) {
    meta.craftaraLocked = false;
    obj.set({
      lockMovementX: false,
      lockMovementY: false,
      selectable: true,
      evented: true,
      hasControls: true,
    });
  }
  obj.setCoords();
}

/** Copy active selection into an in-memory clipboard (JSON). */
export function copySelection(canvas: fabric.Canvas): boolean {
  const active = canvas.getActiveObject();
  if (!active) return false;
  if (isObjectLocked(active) && canvas.getActiveObjects().length === 1) return false;
  clipboardJson = active.toObject([...LAYER_CUSTOM_PROPS]) as Record<string, unknown>;
  return true;
}

/** Paste clipboard as a new object/selection, offset from original. */
export function pasteClipboard(canvas: fabric.Canvas): Promise<boolean> {
  if (!clipboardJson) return Promise.resolve(false);

  return new Promise((resolve) => {
    const enliven = fabric.util.enlivenObjects as (
      objects: object[],
      callback: (enlivened: fabric.Object[]) => void,
      namespace?: string
    ) => void;

    enliven([clipboardJson as object], (objects) => {
      if (!objects?.length) {
        resolve(false);
        return;
      }

      canvas.discardActiveObject();
      objects.forEach((obj) => {
        offsetClone(obj);
        canvas.add(obj);
      });

      if (objects.length === 1) {
        canvas.setActiveObject(objects[0]);
      } else {
        canvas.setActiveObject(new fabric.ActiveSelection(objects, { canvas }));
      }
      canvas.requestRenderAll();
      resolve(true);
    });
  });
}

/** Duplicate active selection via copy + paste. */
export function duplicateSelection(canvas: fabric.Canvas): Promise<boolean> {
  if (!copySelection(canvas)) return Promise.resolve(false);
  return pasteClipboard(canvas);
}

export function nudgeSelection(
  canvas: fabric.Canvas,
  dx: number,
  dy: number
): boolean {
  const targets = editableTargets(canvas);
  if (!targets.length) return false;

  targets.forEach((obj) => {
    obj.set({
      left: (obj.left || 0) + dx,
      top: (obj.top || 0) + dy,
    });
    obj.setCoords();
  });

  const active = canvas.getActiveObject();
  if (active) active.setCoords();
  canvas.requestRenderAll();
  return true;
}

export function selectAllObjects(canvas: fabric.Canvas): boolean {
  const objects = canvas.getObjects().filter((obj) => {
    if (!obj.visible || obj.excludeFromExport) return false;
    if (isCropOverlay(obj) || isMarqueeOverlay(obj)) return false;
    if (isObjectLocked(obj)) return false;
    return true;
  });

  canvas.discardActiveObject();
  if (!objects.length) {
    canvas.requestRenderAll();
    return false;
  }
  if (objects.length === 1) {
    canvas.setActiveObject(objects[0]);
  } else {
    canvas.setActiveObject(new fabric.ActiveSelection(objects, { canvas }));
  }
  canvas.requestRenderAll();
  return true;
}

export function arrangeSelection(
  canvas: fabric.Canvas,
  dir: ArrangeDir
): boolean {
  const obj = canvas.getActiveObject();
  if (!obj || isObjectLocked(obj)) return false;
  if (dir === 'front') canvas.bringToFront(obj);
  if (dir === 'forward') canvas.bringForward(obj);
  if (dir === 'backward') canvas.sendBackwards(obj);
  if (dir === 'back') canvas.sendToBack(obj);
  canvas.requestRenderAll();
  return true;
}

/** Cut = copy + remove active selection. */
export function cutSelection(canvas: fabric.Canvas): boolean {
  if (!copySelection(canvas)) return false;
  const active = canvas.getActiveObjects();
  const removable = active.filter((obj) => !isObjectLocked(obj));
  if (!removable.length) return false;
  removable.forEach((obj) => canvas.remove(obj));
  canvas.discardActiveObject();
  canvas.requestRenderAll();
  return true;
}

export function hasClipboard(): boolean {
  return Boolean(clipboardJson);
}
